import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const STORAGE_KEY = 'mt-cookie-consent-v1'
const STORAGE_VERSION = 1
export const CONSENT_CHANGED_EVENT = 'mt-cookie-consent-changed'

const defaultPreferences = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false
}

const categoryDefinitions = [
  { key: 'essential', required: true },
  { key: 'functional', required: false },
  { key: 'analytics', required: false },
  { key: 'marketing', required: false }
]

export const useCookieConsentStore = defineStore('cookieConsent', () => {
  const preferences = ref({ ...defaultPreferences })
  const lastUpdated = ref(null)
  const hasDecision = ref(false)
  const isSettingsOpen = ref(false)

  const isReadyForStorage = typeof window !== 'undefined' && typeof localStorage !== 'undefined'

  const categories = computed(() => categoryDefinitions)

  const showBanner = computed(() => isReadyForStorage && !hasDecision.value)

  const isAllowed = (key) => Boolean(preferences.value?.[key])

  const persist = () => {
    if (!isReadyForStorage) return
    const payload = {
      version: STORAGE_VERSION,
      preferences: preferences.value,
      updatedAt: new Date().toISOString()
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    hasDecision.value = true
    lastUpdated.value = payload.updatedAt
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { ...preferences.value } }))
  }

  const load = () => {
    if (!isReadyForStorage) return
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed.version !== STORAGE_VERSION || !parsed.preferences) return
      preferences.value = { ...defaultPreferences, ...parsed.preferences, essential: true }
      hasDecision.value = true
      lastUpdated.value = parsed.updatedAt || null
    } catch (e) {
      // swallow parsing errors and start fresh
      preferences.value = { ...defaultPreferences }
      hasDecision.value = false
      lastUpdated.value = null
    }
  }

  const save = (newPreferences = {}) => {
    preferences.value = {
      ...defaultPreferences,
      ...preferences.value,
      ...newPreferences,
      essential: true
    }
    persist()
    closeSettings()
  }

  const acceptAll = () => {
    const allTrue = Object.keys(defaultPreferences).reduce((acc, key) => {
      acc[key] = true
      return acc
    }, {})
    save(allTrue)
  }

  const rejectAll = () => {
    save({
      functional: false,
      analytics: false,
      marketing: false
    })
  }

  const openSettings = () => {
    isSettingsOpen.value = true
  }

  const closeSettings = () => {
    isSettingsOpen.value = false
  }

  // Initialize from LocalStorage on creation
  load()

  return {
    categories,
    preferences,
    lastUpdated,
    hasDecision,
    isSettingsOpen,
    showBanner,
    isAllowed,
    save,
    acceptAll,
    rejectAll,
    openSettings,
    closeSettings
  }
})
